import { instance } from "api/apiBase";
import {
  PrescreeningCreditScore,
  PrescreeningGrossMonthlyIncome,
  PrescreeningQuestionRecord,
  QuestionType
} from "shared/types";
import { Lead } from "views/leads-id/types";

interface PrescreeningQuestionAnswers extends PrescreeningQuestionRecord {
  answers: {
    name: string;
    answer: string;
    type?: QuestionType;
    options?: string[];
  }[];
}

export type PrescreeningStatus =
  | "in_progress"
  | "submitted"
  | "pending_review"
  | "failed"
  | "abandoned"
  | null;

export type PrescreeningRecommendation = "accept" | "review" | "decline";

export type PrescreeningRuleSetType = "review" | "decline";

export interface PrescreeningRuleCondition {
  met: boolean;
  field: string;
  // Numeric criteria threshold is a number; range/yes-no is a string (or
  // string[] for multi-select range conditions).
  value: string | number | string[];
  operator: string;
  // Numeric criteria report a number; range/yes-no report a string.
  applicant_value: string | number;
}

export interface PrescreeningRule {
  id: string;
  matched: boolean;
  conditions: PrescreeningRuleCondition[];
}

export interface PrescreeningRuleReason {
  rule_set_type: PrescreeningRuleSetType;
  rules: PrescreeningRule[];
}

export interface LeadPrescreeningsResponse {
  credit_score: PrescreeningCreditScore | null;
  income?: PrescreeningGrossMonthlyIncome;
  question_answers?: PrescreeningQuestionAnswers;
  status: PrescreeningStatus;
  submitted_at: string | null;
  abandoned_at: string | null;
  ip_address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  identity_verification_enabled: boolean;
  identity_verification?: {
    status: string | null;
    finished: boolean;
  };
  recommendation?: PrescreeningRecommendation | null;
  rule_reasons?: PrescreeningRuleReason[];
}

export const leadApi = {
  assignLead: async ({
    lead_id,
    assignee_ids
  }: {
    lead_id: number;
    assignee_ids: number[];
  }): Promise<Lead> => {
    const response = await instance.put<Lead>(`/portal/v1/leads/${lead_id}`, {
      assignee_ids
    });
    return response.data;
  },

  starLead: async ({
    lead_id,
    starred
  }: {
    lead_id: number;
    starred: boolean;
  }): Promise<Lead> => {
    const { data } = await instance.put<Lead>(
      `/portal/v1/leads/${lead_id}/star`,
      { starred }
    );
    return data;
  },

  getPrescreenings: async (
    leadId: string
  ): Promise<LeadPrescreeningsResponse> => {
    const { data } = await instance.get<LeadPrescreeningsResponse>(
      `/portal/v1/leads/${leadId}/prescreenings`
    );
    return data;
  },

  bulkAssignLeads: async ({
    lead_ids,
    assignee_ids
  }: {
    lead_ids: number[];
    assignee_ids: number[];
  }): Promise<Lead> => {
    return instance.post("/portal/v1/leads/bulk_assign", {
      lead_ids,
      assignee_ids
    });
  },

  bulkChangeStage: async ({
    lead_ids,
    stage_id
  }: {
    lead_ids: number[];
    stage_id: number;
  }): Promise<void> => {
    return instance.post("/portal/v1/leads/bulk_change_stage", {
      lead_ids,
      stage_id
    });
  }
};
